/**
 * Baixar um arquivo de mídia — de verdade.
 *
 * O atributo `download` de um `<a>` é **ignorado quando a URL é de outra
 * origem**. Nossos arquivos ficam no S3, que é outra origem, então
 * `<a href={s3Url} download>` não baixa nada: o navegador simplesmente navega
 * até o arquivo. Com `target="_blank"` junto, abre numa aba nova — que é
 * exatamente o que se via no lightbox, no Lucas e no Inbox.
 *
 * `fetch` + blob também não serve: o S3 direto bloqueia por CORS.
 *
 * O que funciona é passar pela nossa API, que gera uma URL assinada com
 * `Content-Disposition: attachment`. Aí o download acontece mesmo sendo outra
 * origem, porque quem manda baixar é o S3, com o cabeçalho certo.
 *
 * Este helper existe porque a mesma chamada estava copiada em quatro lugares e
 * o quinto — o lightbox — tinha feito diferente, e errado.
 */
import { apiFetch } from "@/lib/api";
import { isEmbedSessionActive } from "@/lib/embedSession";
import { reportError } from "@/lib/errorReporter";

const DOWNLOAD_ROUTE = "/api/inbox/media/download-url";

function navigate(href) {
  const link = document.createElement("a");
  link.href = href;
  link.click();
}

/**
 * @param {string} url — endereço do arquivo no S3
 * @param {string} [filename] — nome com que ele deve chegar ao disco
 * @returns {Promise<void>|undefined}
 */
export function downloadMedia(url, filename) {
  if (!url) return;

  const params = new URLSearchParams({ url });
  if (filename) params.set("filename", filename);

  // App normal: a autenticação é por cookie HttpOnly, então a navegação até a
  // rota carrega a sessão sozinha e a rota redireciona para a URL assinada.
  if (!isEmbedSessionActive()) {
    navigate(`${DOWNLOAD_ROUTE}?${params}`);
    return;
  }

  // Embed: o token só existe em memória e vai no header Authorization. Uma
  // navegação não leva header, e dentro do iframe do parceiro não há cookie,
  // então ela chegava sem credencial (401). Pedimos a URL assinada pelo
  // `apiFetch`, que injeta o bearer, e navegamos direto para o S3.
  params.set("format", "json");
  return apiFetch(`${DOWNLOAD_ROUTE}?${params}`)
    .then(async (response) => {
      if (!response.ok) throw new Error(`download-url respondeu ${response.status}`);
      const { url: signedUrl } = await response.json();
      navigate(signedUrl);
    })
    .catch((error) => {
      reportError("media.download", error, { embed: true });
    });
}

export default downloadMedia;
